// Super-admin operations: client orgs, members, invites.
//
// "Super-admin" here means a user with `user.role='admin'` from
// Better Auth's *admin plugin* (src/src/lib/auth.ts). Not to be
// confused with `member.role='admin'`, which is the organization
// plugin's per-org role.
//
// All endpoints here live under `/auth/v1/*` (Better Auth's
// organization plugin) and authenticate with the BA session cookie,
// NOT the QB JWT — that's why they bypass `api()` and use raw fetch.
//
// BA's create-organization auto-inserts a `member` row for the
// creator with role `owner`. That's how a super-admin gains active-
// org rights on a freshly minted client tenant without any guard
// hacks.

import { ensureSession } from "./auth"
import { api } from "./api"
import { backendUrl } from "./backend-url"
import { authApiPath, isV2Api } from "./api-contract"

export type ClientOrg = {
  id: string
  name: string
  slug: string
  logo: string | null
  createdAt: string
  metadata: string | null
}

export type OrgMember = {
  id: string
  userId: string
  organizationId: string
  role: string
  createdAt: string
  user: {
    id: string
    email: string
    name: string | null
    image: string | null
  }
}

export type OrgInvitation = {
  id: string
  organizationId: string
  email: string
  role: string
  status: string
  expiresAt: string
  inviterId: string
}

/// Error thrown by authFetch on a non-2xx response. Carries the HTTP
/// `status` and Better Auth's machine `code` so callers (e.g. the
/// accept-invitation page) can branch on 400-expired vs 403-wrong-
/// recipient without string-matching the message.
export class AuthFetchError extends Error {
  status: number
  code?: string
  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = "AuthFetchError"
    this.status = status
    this.code = code
  }
}

async function authFetch<T>(
  path: string,
  init: { method?: string; body?: unknown; searchParams?: Record<string, string> } = {},
): Promise<T> {
  const session = await ensureSession()
  if (!session) throw new Error("Not signed in")
  const qs = init.searchParams
    ? "?" + new URLSearchParams(init.searchParams).toString()
    : ""
  const res = await fetch(backendUrl(`${path}${qs}`), {
    method: init.method ?? "GET",
    headers: {
      Accept: "application/json",
      ...(init.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    credentials: "include",
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { message?: string; error?: string; code?: string }
      | null
    throw new AuthFetchError(
      body?.message ?? body?.error ?? `${res.status} ${res.statusText}`,
      res.status,
      body?.code,
    )
  }
  return (await res.json()) as T
}

/// Lists organizations the caller is a member of. For a platform admin,
/// this includes every org they've created (BA auto-adds them as owner)
/// plus any they've been explicitly invited to. Orgs the admin has
/// never touched are NOT returned — listing those requires a custom
/// quickback action which is deferred to a follow-up.
export async function listClients(): Promise<ClientOrg[]> {
  return authFetch<ClientOrg[]>(authApiPath("/organization/list"))
}

/// Creates an org. BA's plugin atomically inserts a `member` row with
/// role `owner` for the caller, so the platform admin can immediately
/// switch into the new org and create teams/events through the normal
/// admin UI.
export async function createClient(input: {
  name: string
  slug: string
  logo?: string
}): Promise<ClientOrg> {
  return authFetch<ClientOrg>(authApiPath("/organization/create"), {
    method: "POST",
    body: input,
  })
}

export async function listClientMembers(
  organizationId: string,
): Promise<{ members: OrgMember[]; invitations: OrgInvitation[] }> {
  // BA's `get-full-organization` returns the org + members + invitations
  // in one shot. Cheaper than two round trips and matches what the
  // session-hydration path uses server-side.
  const org = await authFetch<{
    members?: OrgMember[]
    invitations?: OrgInvitation[]
  }>(authApiPath("/organization/get-full-organization"), {
    searchParams: { organizationId },
  })
  return {
    members: org.members ?? [],
    invitations: (org.invitations ?? []).filter((i) => i.status === "pending"),
  }
}

/// Invites a user by email. BA writes a row in the `invitation` table
/// and, if SES env vars are configured, sends the invitation email
/// (configured in src/src/lib/auth.ts). If SES is unset the row is
/// still created — the caller can copy the invite URL from the
/// returned invitation id.
export async function inviteToClient(input: {
  organizationId: string
  email: string
  role: "owner" | "admin" | "member"
}): Promise<OrgInvitation> {
  return authFetch<OrgInvitation>(authApiPath("/organization/invite-member"), {
    method: "POST",
    body: input,
  })
}

/// Pre-populates the invited person's display name on their (passwordless)
/// BA user row so the accept flow doesn't have to ask — see the
/// `org-invite-user-name` action. Best-effort: a failure here must never
/// block the invite itself, so callers fire it after a successful invite
/// and swallow errors.
export async function setInviteUserName(email: string, name: string): Promise<void> {
  try {
    await api("/api/v1/org-invite-user-name", {
      method: "POST",
      body: { email, name },
    })
  } catch {
    // Nicety only — the invitee can still set their name on the accept page.
  }
}

/// Changes an existing member's org role. BA's organization plugin
/// enforces the actor's own role is high enough (owner > admin > member)
/// and rejects demoting the last owner server-side.
export async function updateMemberRole(input: {
  organizationId: string
  memberId: string
  role: "owner" | "admin" | "member"
}): Promise<OrgMember> {
  return authFetch<OrgMember>(authApiPath("/organization/update-member-role"), {
    method: "POST",
    body: input,
  })
}

/// Removes a member from the org. `memberIdOrEmail` accepts either the
/// `member.id` or the user's email. BA refuses to remove the last owner.
export async function removeMember(input: {
  organizationId: string
  memberIdOrEmail: string
}): Promise<{ success?: boolean }> {
  return authFetch<{ success?: boolean }>(authApiPath("/organization/remove-member"), {
    method: "POST",
    body: input,
  })
}

/// Cancels a pending invitation. There is no dedicated resend endpoint —
/// to resend, call `inviteToClient` again with the same email (BA is
/// idempotent on the pending row).
export async function cancelInvitation(invitationId: string): Promise<{ success?: boolean }> {
  return authFetch<{ success?: boolean }>(authApiPath("/organization/cancel-invitation"), {
    method: "POST",
    body: { invitationId },
  })
}

export function buildInviteUrl(invitationId: string): string {
  // First-party accept page — AcceptInvitationPage at /accept-invitation/$id
  // (router.tsx), served by the admin SPA at the aaa.attend.vip root. The
  // emailed invite (src/src/lib/auth.ts, compiler-generated from ACCOUNT_URL)
  // resolves to this same path once ACCOUNT_URL is the bare origin.
  if (typeof window === "undefined") return `/accept-invitation/${invitationId}`
  return `${window.location.origin}/accept-invitation/${invitationId}`
}

/// Full invitation detail from BA's get-invitation (org plugin). Only
/// readable once signed in AS the invited email — the server 403s
/// (code YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION) otherwise, and
/// 400s ("Invitation not found!") when missing / expired / already
/// actioned. See src/node_modules/.../organization/routes/crud-invites.
export type InvitationDetail = {
  id: string
  organizationId: string
  email: string
  role: string
  status: string
  expiresAt: string
  inviterId: string
  organizationName: string
  organizationSlug: string
  inviterEmail: string
}

/// Public, pre-auth preview of an invitation — powers the accept page's
/// email prefill + "You've been invited to {org}" context before sign-in,
/// AND the terminal screens for used/expired/revoked links. Backed by the
/// PUBLIC `POST /api/v1/org-invite-preview` action (reads the BA invitation
/// table; gated only by the unguessable id). Never throws — a network/parse
/// failure resolves to `{ status: "not_found" }` so the page degrades to
/// manual email entry.
export type OrgInvitePreview =
  | {
      status: "pending"
      email: string
      role: string | null
      organizationName: string | null
    }
  | { status: "accepted" | "expired" | "revoked"; organizationName: string | null }
  | { status: "not_found" }

export async function getOrgInvitePreview(
  invitationId: string,
): Promise<OrgInvitePreview> {
  try {
    const res = await fetch(backendUrl(isV2Api
      ? `/api/v2/organization-invitations/${encodeURIComponent(invitationId)}/preview`
      : "/api/v1/org-invite-preview"), {
      method: isV2Api ? "GET" : "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: isV2Api ? undefined : JSON.stringify({ invitationId }),
      credentials: "omit",
    })
    if (!res.ok) return { status: "not_found" }
    const data = (await res.json()) as
      | OrgInvitePreview
      | { found: boolean; email?: string; role?: string | null; organizationName?: string | null }
    // Back-compat: the previously-deployed action returned `{ found, … }`
    // rather than `{ status }`. Until the new action is live in prod (dev
    // proxies there), normalize the old shape so prefill keeps working.
    if ("found" in data) {
      return data.found
        ? {
            status: "pending",
            email: data.email ?? "",
            role: data.role ?? null,
            organizationName: data.organizationName ?? null,
          }
        : { status: "not_found" }
    }
    return data
  } catch {
    return { status: "not_found" }
  }
}

export async function getInvitation(invitationId: string): Promise<InvitationDetail> {
  return authFetch<InvitationDetail>(authApiPath("/organization/get-invitation"), {
    searchParams: { id: invitationId },
  })
}

/// Accepts the invitation for the *current* session user. BA inserts the
/// `member` row and marks the invitation accepted; it enforces that the
/// session email matches the invited email server-side.
export async function acceptInvitation(invitationId: string): Promise<void> {
  await authFetch(authApiPath("/organization/accept-invitation"), {
    method: "POST",
    body: { invitationId },
  })
}

export async function rejectInvitation(invitationId: string): Promise<void> {
  await authFetch(authApiPath("/organization/reject-invitation"), {
    method: "POST",
    body: { invitationId },
  })
}

/// Switches the BA session's active organization to `organizationId`.
/// Used from the admin clients page so the super-admin can pivot into
/// a freshly-created client org and use the regular event UI.
/// Callers should also bust the JWT cache (via invalidateJwt in auth.ts)
/// and reload any zustand stores that hold org-scoped data
/// (`useEvents`, `useUserOrgs`).
export async function switchActiveClient(organizationId: string): Promise<void> {
  await authFetch(authApiPath("/organization/set-active"), {
    method: "POST",
    body: { organizationId },
  })
}
